import { useRef, useState } from 'react';

import { useForceReRender } from './useForceReRender';

type CallbackProps = {
  done: boolean;
  timer: number;
  progress: number;
};

type Config = {
  /** The callback will be triggered periodically with the progress values */
  enableProgress?: boolean;
  /** The interval in milliseconds in which the progress values will be reported */
  progressInterval?: number;
};

type StartTimerProp = {
  /** In milliseconds */
  duration: number;
};

export const useTimer = (callback: (tick: CallbackProps) => void, config?: Config) => {
  const { progressInterval = 1000, enableProgress = true } = config || {};

  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const timerInterval = useRef<number | null>(null);
  const finishTimer = useRef<number | null>(null);
  const timerRef = useRef<number>(0);
  const progressRef = useRef<number>(0);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const reRender = useForceReRender();

  const setTimer = (time: number, duration: number) => {
    timerRef.current = time;
    progressRef.current = (duration - time) / duration;
    reRender();
  };

  const clearTimer = () => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }
    if (finishTimer.current) {
      clearTimeout(finishTimer.current);
    }
    setIsRunning(false);
  };

  const startTimer = ({ duration }: StartTimerProp) => {
    clearTimer();
    if (duration < progressInterval) {
      console.error(
        `Duration (${duration}) should be greater than progress interval (${progressInterval})`,
      );
      return;
    }
    setTimer(duration, duration);
    timerInterval.current = setInterval(() => {
      setTimer(timerRef.current - progressInterval, duration);
      if (enableProgress) {
        callbackRef.current({
          done: false,
          timer: timerRef.current,
          progress: timerRef.current / duration,
        });
      }
      reRender();
    }, progressInterval);

    finishTimer.current = setTimeout(() => {
      setTimer(0, duration);
      callbackRef.current({ done: true, timer: timerRef.current, progress: 1 });
      clearTimer();
      setIsFinished(true);
    }, duration);
    setIsRunning(true);
    setIsFinished(false);
  };

  return {
    startTimer,
    clearTimer,
    timer: timerRef.current,
    progress: progressRef.current,
    isRunning,
    isFinished,
  };
};
